import { useRef, useState } from "react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_SIZE = 15 * 1024 * 1024; // 15 MB — iPhone photos can be large

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Convert a URL returned by /api/uploads/... to a full URL for display
export const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

export default function ImagePicker({ value, onChange, testIdPrefix = "img" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error("Kuva on liian iso (max 15 MB)");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file, file.name || "upload");
      // NOTE: do NOT set Content-Type header manually — axios/browser must
      // set it with the correct multipart boundary. Setting it here breaks
      // uploads on iOS Safari (boundary missing → server can't parse).
      const r = await api.post("/admin/uploads", form);
      onChange(r.data.url);
      const orig = r.data.original_size;
      const now = r.data.size;
      if (orig && now && orig > now) {
        const savedPct = Math.round((1 - now / orig) * 100);
        toast.success(
          `Ladattu • ${formatBytes(orig)} → ${formatBytes(now)} (−${savedPct} %)`
        );
      } else {
        toast.success("Ladattu");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const displayUrl = resolveImageUrl(value);

  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL tai lataa tiedosto →"
          className="flex-1 bg-transparent border border-border px-3 py-2 outline-none focus:border-[hsl(var(--primary))]"
          data-testid={`${testIdPrefix}-url`}
        />
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          className="btn-outline shrink-0 !py-2"
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          {uploading ? (
            <Loader2 strokeWidth={1.5} className="w-4 h-4 animate-spin" />
          ) : (
            <Upload strokeWidth={1.5} className="w-4 h-4" />
          )}
          Lataa
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
      </div>
      {displayUrl && (
        <img
          src={displayUrl}
          alt=""
          className="w-32 h-32 object-cover border border-border rounded-md"
        />
      )}
    </div>
  );
}
